import { useState, useRef } from "react";
import PropTypes from "prop-types";
import SwiperCore, { Navigation, Pagination, A11y, Thumbs, FreeMode } from "swiper";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import { isCrawlerBot } from "@helpers/browser";
import { isPreorder } from "@helpers/product";
import useDeviceDetect from "@hooks/useDeviceDetect";
import MobileGalleryPopup from "@components/MobileGalleryPopup";

import { ActionType } from "@constants/ActionType";
import { ComponentType } from "@constants/ComponentType";
import { Country } from "@constants/Country";
import ImagesSwiper from "@components/gallery/ImagesSwiper";
import ThumbnailSidebar from "@components/gallery/ThumbnailSidebar";

import IconHeart from "@svgs/heart.svg";

import "swiper/swiper.min.css";
import "swiper/swiper-bundle.min.css";

import "./gallery/styles.scss";

SwiperCore.use([Navigation, Pagination, A11y, FreeMode, Thumbs]);

const USP_EXCLUDED_PRODUCT_TYPES = ["Lifestyle"];

function Gallery({
  images = [],
  isUS,
  isGiftCard,
  available,
  isInWishlist,
  toggleWishlist,
  className = "",
  componentType,
  video = {},
  product = {},
  selectedVariant = {},
  countryCode = "",
  isGiftProduct = false,
  topPosition = null,
  children,
}) {
  const { isMobile } = useDeviceDetect();
  const swiperRef = useRef(null);

  const [activeIdx, setActiveIdx] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(images?.length > 0 ? images[0] : null);

  const showEmergency =
    (componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS) &&
    !product.tags?.includes("Components:Solid Gold") &&
    !isGiftProduct;

  const showUSPImage =
    componentType !== "CP_BYOB" &&
    !product.type?.some((t) => USP_EXCLUDED_PRODUCT_TYPES.includes(t));

  const hasVideo = !!video && video.url && !isCrawlerBot();

  const openPopup = (img) => {
    if (!isMobile) return;
    // Make sure we don't trigger the popup on other components than PDP
    if (componentType === ComponentType.PDP) {
      setActiveImage(img);
      setIsPopupOpen(true);
    }
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const hasVariantAvailable =
    selectedVariant?.availableForSale === true && selectedVariant?.inventoryPolicy === "DENY";
  const showPreOrderFomo =
    isPreorder(product, selectedVariant) && countryCode === Country.US && !hasVariantAvailable;
  const showWishlist = isUS && available && !isGiftCard && !isGiftProduct;

  const slideToIdx = (idx) => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(idx + (hasVideo ? 1 : 0));
    }
  };

  if (!images) {
    return null;
  }

  const handleSwiperActiveIndexChange = (swiper) => {
    if (componentType === ComponentType.CP_BYOB) return;
    const removeCount = hasVideo ? 1 : 0;
    const contentIdx = swiper.realIndex - removeCount;
    setActiveImage(images[Math.max(contentIdx, 0)]);
    setActiveIdx(Math.max(contentIdx, 0));
  };

  return (
    <>
      {(componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS) && (
        <ThumbnailSidebar
          images={images}
          activeIdx={activeIdx}
          onThumbClick={slideToIdx}
          topPosition={topPosition}
          productTags={product.tags}
          componentType={componentType}
          showUSPImage={showUSPImage}
        />
      )}

      <div
        style={!isMobile ? topPosition : undefined}
        className={`${cn(
          "relative",
          isMobile ? "gallery_m w-full overflow-hidden" : "flex wrap mr-[50px] w-[40%]",
          componentType === ComponentType.PDP_ADS && !isMobile && "mt-[25px]",
          componentType === ComponentType.PDP_ADS && isMobile && "pl-[15px]",
          componentType === ComponentType.PDP && isMobile && "px-[15px]",
          className
        )}`}
      >
        {showWishlist && (
          <button
            className="absolute top-[10px] right-[24px] z-10 flex justify-center items-center desktop:right-[20px] desktop:top-[20px]"
            onClick={() => toggleWishlist(isInWishlist ? ActionType.REMOVE : ActionType.ADD)}
            type="button"
            data-testid={isInWishlist ? "wishlist-icon-full" : "wishlist-icon-empty"}
          >
            <IconHeart
              width={isMobile ? 25 : 30}
              height={isMobile ? 25 : 30}
              fill={isInWishlist ? "#000000" : "none"}
              stroke="#000000"
            />
          </button>
        )}

        <ImagesSwiper
          images={images}
          hasVideo={hasVideo}
          video={video}
          componentType={componentType}
          showEmergency={showEmergency}
          product={product}
          showPreOrderFomo={showPreOrderFomo}
          onSlideClick={openPopup}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onActiveIndexChange={handleSwiperActiveIndexChange}
          showUSPImage={showUSPImage}
        />

        {isPopupOpen && (
          <MobileGalleryPopup
            images={images}
            activeImage={activeImage}
            setActiveImage={setActiveImage}
            closePopup={closePopup}
          />
        )}

        {children}
      </div>
    </>
  );
}

Gallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      altText: PropTypes.string,
    })
  ),
  isUS: PropTypes.bool,
  isGiftCard: PropTypes.bool,
  available: PropTypes.bool,
  isInWishlist: PropTypes.bool,
  toggleWishlist: PropTypes.func,
  className: PropTypes.string,
  componentType: PropTypes.string,
  video: PropTypes.shape({
    url: PropTypes.string,
  }),
  product: PropTypes.shape({
    tags: PropTypes.arrayOf(PropTypes.string),
    productVariants: PropTypes.arrayOf(PropTypes.object),
  }),
  selectedVariant: PropTypes.shape({
    availableForSale: PropTypes.bool,
    inventoryPolicy: PropTypes.string,
  }),
  countryCode: PropTypes.string,
  isGiftProduct: PropTypes.bool,
  topPosition: PropTypes.object,
  children: PropTypes.node,
};

export default withALErrorBoundary({
  name: "Gallery",
  priority: "P2",
})(Gallery);
